import type {
  AiAdminConfig,
  AiCatalogModel,
  AiCatalogModelWrite,
  AiModelCatalog,
  AiModelRoute,
  AiOrgRouting,
  AiProviderCreate,
  AiProviderConnection,
  AiProviderModels,
  AiProviderUpdate,
  AiProviderVerify,
  AiRouteClass,
  AiRoutingMode,
  AiTestResult,
} from "@backstory/protocol";
import { request } from "../../api";

const enc = encodeURIComponent;

/**
 * One client per scope. The platform operator and an organization administer AI
 * through the same endpoints under different prefixes, so the difference between
 * them is a base path and a query key rather than a second implementation.
 */
export type AiAdminClient = {
  keys: { config: readonly unknown[]; models: (id: string) => readonly unknown[]; catalog: readonly unknown[] };
  config: () => Promise<AiAdminConfig>;
  /**
   * The priced model list. Only the platform has one, so this is absent on an
   * organization's client: it pays its own vendor and no price of ours applies.
   */
  catalog?: () => Promise<AiModelCatalog>;
  saveCatalogModel?: (body: AiCatalogModelWrite, id?: string) => Promise<AiCatalogModel>;
  deleteCatalogModel?: (id: string) => Promise<{ ok: boolean }>;
  /** What this connection can route to, answered by the gateway that holds its key. */
  providerModels: (id: string) => Promise<AiProviderModels>;
  createProvider: (body: AiProviderCreate) => Promise<AiProviderConnection>;
  /** Proves credentials work before any of them are stored. */
  verifyProvider: (body: AiProviderVerify) => Promise<AiTestResult>;
  updateProvider: (id: string, update: AiProviderUpdate) => Promise<AiProviderConnection>;
  clearProvider: (id: string) => Promise<AiProviderConnection>;
  deleteProvider: (id: string) => Promise<{ ok: boolean }>;
  testProvider: (id: string) => Promise<AiTestResult>;
  updateRoute: (route: AiModelRoute) => Promise<AiModelRoute>;
  testRoute: (routeClass: AiRouteClass) => Promise<AiTestResult>;
  /** Organization scope only; the platform has no source to choose. */
  setRoutingMode: (mode: AiRoutingMode) => Promise<AiOrgRouting>;
};

function client(basePath: string, keyScope: readonly unknown[]): AiAdminClient {
  return {
    keys: {
      config: [...keyScope, "config"],
      models: (id) => [...keyScope, "providers", id, "models"],
      catalog: [...keyScope, "catalog"],
    },
    config: () => request<AiAdminConfig>(`${basePath}/config`),
    providerModels: (id) => request<AiProviderModels>(`${basePath}/providers/${enc(id)}/models`),
    createProvider: (body) => request<AiProviderConnection>(`${basePath}/providers`, { method: "POST", body }),
    verifyProvider: (body) => request<AiTestResult>(`${basePath}/providers/verify`, { method: "POST", body }),
    updateProvider: (id, update) => request<AiProviderConnection>(`${basePath}/providers/${enc(id)}`, { method: "PUT", body: update }),
    clearProvider: (id) => request<AiProviderConnection>(`${basePath}/providers/${enc(id)}/credentials`, { method: "DELETE" }),
    deleteProvider: (id) => request<{ ok: boolean }>(`${basePath}/providers/${enc(id)}`, { method: "DELETE" }),
    testProvider: (id) => request<AiTestResult>(`${basePath}/providers/${enc(id)}/test`, { method: "POST", body: {} }),
    updateRoute: (route) => request<AiModelRoute>(`${basePath}/routes/${enc(route.class)}`, { method: "PUT", body: route }),
    testRoute: (routeClass) => request<AiTestResult>(`${basePath}/routes/${enc(routeClass)}/test`, { method: "POST", body: {} }),
    setRoutingMode: (mode) => request<AiOrgRouting>(`${basePath}/mode`, { method: "PUT", body: { mode } }),
  };
}

/** The deployment's own providers and routing, administered by staff. */
export const platformAiAdmin: AiAdminClient = {
  ...client("/v1/admin/ai", ["admin", "ai"]),
  catalog: () => request<AiModelCatalog>("/v1/admin/ai/models"),
  saveCatalogModel: (body, id) =>
    id
      ? request<AiCatalogModel>(`/v1/admin/ai/models/${enc(id)}`, { method: "PUT", body })
      : request<AiCatalogModel>("/v1/admin/ai/models", { method: "POST", body }),
  deleteCatalogModel: (id) => request<{ ok: boolean }>(`/v1/admin/ai/models/${enc(id)}`, { method: "DELETE" }),
};

/** One organization's own providers and routing. */
export function orgAiAdmin(orgId: string): AiAdminClient {
  return client(`/v1/orgs/${enc(orgId)}/ai/admin`, ["orgs", orgId, "ai", "admin"]);
}

export const aiAdminApi = platformAiAdmin;
export const aiAdminKeys = platformAiAdmin.keys;
